import React from "react";
import { Link, useNavigate } from "react-router-dom";
import ClientShell from "@/components/ClientShell";
import AuthImage from "@/components/AuthImage";
import { useCategories } from "@/hooks/useCategories";
import { MagnifyingGlass, Camera, Package, Lightning } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

export default function ClientHome() {
  const { user, isGuest } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = React.useState("");
  const { categories, loading } = useCategories(isGuest);

  const onSubmit = (e) => {
    e.preventDefault();
    const term = q.trim();
    const match = categories.find((c) => c.label.toLowerCase().includes(term.toLowerCase()));
    if (match && term) {
      navigate(`/category/${match.category_id}`);
    }
  };

  return (
    <ClientShell>
      {/* Fixed search bar */}
      <div className="sticky top-16 -mx-4 md:-mx-8 z-30 bg-zinc-50/95 backdrop-blur-sm px-4 md:px-8 py-3 border-b border-zinc-200">
        <form onSubmit={onSubmit} className="max-w-3xl mx-auto relative">
          <MagnifyingGlass size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            data-testid="material-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search for material"
            className="w-full pl-12 pr-14 py-3.5 bg-white border border-zinc-200 focus:border-zinc-900 outline-none transition-colors text-[15px]"
          />
          <button
            type="button"
            data-testid="image-search-button"
            title="Image search (coming soon)"
            className="absolute right-3 top-1/2 -translate-y-1/2 p-2 text-zinc-400 hover:text-[#0047AB] transition-colors"
            onClick={() => {}}
          >
            <Camera size={20} weight="regular" />
          </button>
        </form>
      </div>

      {/* Greeting */}
      <div className="pt-6 md:pt-8">
        <div className="mt-tiny text-[#FF5A1F]">{isGuest ? "Welcome to Ston Link" : "Welcome back"}</div>
        <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight text-zinc-900 mt-2">
          {isGuest ? "Browse our catalog." : `Hi, ${user?.name?.split(" ")[0] || "there"}.`}
        </h1>
        <p className="mt-2 text-zinc-600 text-sm">
          {isGuest
            ? "Explore categories and products. Sign in to request quotes, download specs and track orders."
            : "Pick a category to browse or start a quote request."}
        </p>
      </div>

      {/* Quick Inquiry CTA (same as mobile) */}
      {!isGuest && (
        <button
          type="button"
          data-testid="quick-inquiry-cta"
          onClick={() => navigate("/quotations/quick")}
          className="mt-6 w-full group flex items-center gap-4 bg-zinc-900 text-white rounded-2xl p-4 md:p-5 hover:bg-zinc-800 transition-colors text-left"
        >
          <div className="grid place-items-center w-11 h-11 rounded-xl bg-[#FF5A1F]/20 shrink-0">
            <Lightning size={22} weight="fill" className="text-[#FF5A1F]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-base md:text-lg leading-tight">Quick Inquiry</div>
            <div className="text-sm text-zinc-300">On site? Send a fast quote request with photos.</div>
          </div>
          <span className="text-[#FF5A1F] text-sm font-semibold shrink-0 group-hover:translate-x-0.5 transition-transform">Start →</span>
        </button>
      )}

      {/* Categories grid */}
      <div className="mt-8 mb-6 flex items-baseline justify-between">
        <h2 className="font-display text-xl md:text-2xl font-bold text-zinc-900">Shop by category</h2>
        <div className="mt-tiny text-zinc-400">{loading ? "…" : `${categories.length} categories`}</div>
      </div>

      <div
        className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4"
        data-testid="categories-grid"
      >
        {loading && categories.length === 0
          ? ["s-1", "s-2", "s-3", "s-4", "s-5"].map((k) => (
              <div key={k} className="aspect-square bg-white border border-zinc-200 animate-pulse" />
            ))
          : categories.map((c) => (
              <Link
                key={c.category_id}
                to={`/category/${c.category_id}`}
                data-testid={`category-card-${c.category_id}`}
                className="group relative aspect-square overflow-hidden border border-zinc-200 hover:border-zinc-900 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg"
                style={{ borderRadius: "10px" }}
              >
                {/* Full-bleed image */}
                {c.icon_path ? (
                  <AuthImage
                    path={c.icon_path}
                    alt={c.label}
                    guest={isGuest}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    fallback={
                      <div className={`absolute inset-0 ${c.blob} grid place-items-center`}>
                        <Package size={48} weight="duotone" className={c.accent_fg} />
                      </div>
                    }
                  />
                ) : c.image_url ? (
                  <img
                    src={c.image_url}
                    alt={c.label}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                ) : (
                  <div className={`absolute inset-0 ${c.blob} grid place-items-center`}>
                    <Package size={48} weight="duotone" className={c.accent_fg} />
                  </div>
                )}

                {/* Dark gradient overlay for legibility */}
                <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/85 via-black/40 to-transparent pointer-events-none" />

                {/* Category label */}
                <div className="absolute inset-x-0 bottom-0 p-3 md:p-4">
                  <div className="font-display font-bold text-white text-base md:text-lg leading-tight drop-shadow-lg">
                    {c.label}
                  </div>
                </div>
              </Link>
            ))}
      </div>
    </ClientShell>
  );
}
